import { useMemo } from 'react';

import {
  ShowbieFeatureName,
  getMinVersionForFeature,
} from '../../showbie-common/config/showbieFeatureVersions';
import { versionToNumber } from '../../util/semver';
import { useShowbieAppInfo } from './useShowbieAppInfo';

/**
 * Check if current version >= minimum version.
 */
function isVersionSupported(
  currentVersion: string,
  minVersion: string
): boolean {
  return versionToNumber(currentVersion) >= versionToNumber(minVersion);
}

/**
 * Check if a feature is supported by the current Showbie app version.
 *
 * @param feature - Feature name to check
 * @returns True if supported, false otherwise (including when app info unavailable)
 *
 * @example
 * ```typescript
 * const canDownload = useShowbieFeatureSupport(ShowbieFeatureName.QuizLibraryDownloadExport);
 * ```
 */
export function useShowbieFeatureSupport(feature: ShowbieFeatureName): boolean {
  const appInfo = useShowbieAppInfo();

  return useMemo(() => {
    // If we don't have app info, assume it's not supported
    if (!appInfo.version || !appInfo.name || !appInfo.environment) {
      return false;
    }

    const minVersion = getMinVersionForFeature({
      feature,
      appName: appInfo.name,
      appEnv: appInfo.environment,
    });

    return isVersionSupported(appInfo.version, minVersion);
  }, [feature, appInfo.version, appInfo.name, appInfo.environment]);
}
